import { Avatar, AvatarFallback, AvatarBadge } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { relativeTime, truncate } from "@/lib/format";
import type { Inbox } from "@/types/inbox";

interface InboxListItemProps {
  inbox: Inbox;
  isSelected: boolean;
  onClick: () => void;
}

const statusDotColor: Record<string, string> = {
  open: "bg-green-500",
  pending: "bg-amber-500",
  resolved: "bg-slate-400",
};

function getInitials(name?: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return parts[0].slice(0, 2).toUpperCase();
}

export function InboxListItem({
  inbox,
  isSelected,
  onClick,
}: InboxListItemProps) {
  const contactName = inbox.contact_name || `Contato #${inbox.id_contact}`;
  const lastMessage = inbox.last_message_content
    ? truncate(inbox.last_message_content, 50)
    : undefined;
  const isInbound = inbox.last_message_direction === "inbound";

  return (
    <button
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors",
        "hover:bg-accent",
        isSelected && "bg-accent",
      )}
    >
      <div className="relative shrink-0">
        <Avatar size="lg">
          <AvatarFallback className="text-xs font-medium">
            {getInitials(contactName)}
          </AvatarFallback>
        </Avatar>
        {inbox.channel_type === "whatsapp" && (
          <AvatarBadge className="bg-green-500 text-white">
            <svg viewBox="0 0 24 24" fill="currentColor" className="h-2 w-2">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
            </svg>
          </AvatarBadge>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span
            className={cn(
              "truncate text-sm",
              isInbound ? "font-semibold" : "font-medium",
            )}
          >
            {contactName}
          </span>
          <span className="shrink-0 text-[11px] text-muted-foreground">
            {relativeTime(inbox.last_message_at || inbox.datetime_add)}
          </span>
        </div>
        <div className="mt-0.5 flex items-center gap-2">
          <span
            className={cn(
              "h-2 w-2 shrink-0 rounded-full",
              statusDotColor[inbox.status] || "bg-slate-400",
            )}
          />
          {lastMessage ? (
            <p
              className={cn(
                "truncate text-xs text-muted-foreground",
                isInbound && "font-medium text-foreground/80",
              )}
            >
              {lastMessage}
            </p>
          ) : (
            <p className="truncate text-xs text-muted-foreground italic">
              Sem mensagens
            </p>
          )}
        </div>
      </div>
    </button>
  );
}
